import React, { SyntheticEvent, useEffect, useState } from 'react';
import { ToastContentProps } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'antd';
import cx from 'classnames';

import {
  checkIsErrorNotification,
  getNotificationIcon,
  getNotificationRedirectURL,
} from 'utils/notificationUtils';
import { getDateFromNow } from 'utils/dateUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import BaseToast from 'components/wrappers/BaseToast';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { setNotificationReadStatus } from 'redux/reducers/notificationSlice';
import { BackofficeNotificationStreamModel } from 'api/backofficeNotificationApiClient';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';

import styles from './PopupNotificationCard.module.less';

type OwnProps = {
  notification: BackofficeNotificationStreamModel;
};

// ToastContentProps are embeded by toast when using custom component
type Props = OwnProps & Partial<ToastContentProps>;

export default function PopupNotificationCard(props: Props) {
  const { notification, closeToast: closeNotification } = props;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [notificationInfo, setNotificationInfo] = useState(notification);
  const {
    type: notificationType,
    createdDate,
    notificationId,
    isNew,
    description,
    title,
  } = notificationInfo;
  const notificationIcon = getNotificationIcon(notificationType);
  const isErrorNotification = checkIsErrorNotification(notificationType);
  const isAdmin = useAppSelector(isAdminSelector);

  useEffect(() => {
    return () => {
      // remove from popup notifications container
      BaseToast.dismiss(notificationId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markNotificationAsNew = async (isNew: boolean) => {
    const result = await dispatch(
      setNotificationReadStatus({
        notificationId,
        isNew,
        isUserAdmin: isAdmin,
      }),
    );

    if (setNotificationReadStatus.fulfilled.match(result)) {
      setNotificationInfo((notification) => ({ ...notification, isNew }));
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const toggleNotificationReadStatus = (event: SyntheticEvent) => {
    event.stopPropagation();

    markNotificationAsNew(!isNew);
  };

  const handleNotificationClick = () => {
    const url = getNotificationRedirectURL(notification);

    isNew && markNotificationAsNew(false);

    if (!url) return;

    closeNotification && closeNotification();

    navigate(url);
  };

  const handleCloseNotificationClick = (event: SyntheticEvent) => {
    event.stopPropagation();

    isNew && markNotificationAsNew(false);

    closeNotification && closeNotification();
  };

  return (
    <div
      data-id={`notification-popup-${notificationId}`}
      className={cx('notification-card', styles.notifyCard, {
        [styles.notifyCardNew]: isNew,
        [styles.notifyCardError]: isErrorNotification,
      })}
      onClick={handleNotificationClick}
    >
      <div className={styles.notifyCardIcon}>{notificationIcon}</div>

      <div className={styles.notifyCardContent}>
        <div className={styles.notifyCardTime}>
          <span className="icon icon-clock" />

          <span>{getDateFromNow(createdDate)}</span>

          <Tooltip
            overlayClassName="light-tooltip"
            title={isNew ? 'Mark as read' : 'Mark as unread'}
            trigger={['hover', 'focus']}
            placement="right"
            getPopupContainer={(triggerNode) =>
              triggerNode.parentNode as HTMLElement
            }
          >
            <span
              className={styles.notifyCardPoint}
              onClick={toggleNotificationReadStatus}
            />
          </Tooltip>
        </div>

        <div className={styles.notifyCardTitle}>{title}</div>

        <div className={styles.notifyCardDescription}>{description}</div>
      </div>

      <Tooltip
        overlayClassName="light-tooltip"
        title="Remove notification"
        trigger={['hover', 'focus']}
        placement="left"
        getPopupContainer={(triggerNode) =>
          triggerNode.parentNode as HTMLElement
        }
      >
        <div
          className={styles.notifyCardClose}
          onClick={handleCloseNotificationClick}
        >
          <span className="icon icon-close-sm"></span>
        </div>
      </Tooltip>
    </div>
  );
}
